import React, { useState } from 'react';
import {
  Portal,
  Modal,
  Button,
  Surface,
  Text,
  TextInput,
} from 'react-native-paper';
import styled from '@emotion/native';
import { black, disabledGrey, sand } from '@/shared/styles/colors';
import { EnvironmentName } from '@/shared/config';
import { ViewStyle } from 'react-native';
import useConfigStore from '@/stores/useConfigStore';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

const DevMenuModal = () => {
  const {
    environment,
    setEnvironment,
    isDevMenuOpen,
    setIsDevMenuOpen,
    localIP,
    setLocalIP,
  } = useConfigStore();

  const [selectedEnvironment, setSelectedEnvironment] =
    useState<EnvironmentName>(environment.name);
  const [inputIP, setInputIP] = useState(localIP);

  const isEnvironmentChanged =
    environment.name !== selectedEnvironment || inputIP !== localIP;

  const handleDismiss = () => {
    setIsDevMenuOpen(false);

    if (isEnvironmentChanged) {
      // Slight delay for modal to close before env switch
      setTimeout(async () => {
        await setLocalIP(inputIP);
        setEnvironment(selectedEnvironment);
      }, 500);
    }
  };

  const onPressSelectEnvironment = (name: EnvironmentName) => {
    setSelectedEnvironment(name);
  };

  return (
    <Portal>
      <Modal
        visible={isDevMenuOpen}
        onDismiss={handleDismiss}
        contentContainerStyle={modalContentContainerStyle}
      >
        <Overlay>
          <MenuContainer elevation={3}>
            <KeyboardAwareScrollView
              style={{ width: '100%' }}
              contentContainerStyle={{ flexGrow: 1 }}
            >
              <CloseButton onPress={handleDismiss}>
                <CloseIcon source={require('@/assets/images/CloseIcon.png')} />
              </CloseButton>

              <Title>Dev Menu</Title>
              <CopyRow>
                <Label>Current Env:</Label>
                <BodyText>{environment.name}</BodyText>
              </CopyRow>
              <CopyRow>
                <Label>Selected Env:</Label>
                <BodyText>{selectedEnvironment}</BodyText>
              </CopyRow>
              <StyledButton
                mode="contained"
                onPress={() => onPressSelectEnvironment('production')}
              >
                Production
              </StyledButton>

              <StyledButton
                mode="contained"
                onPress={() => onPressSelectEnvironment('staging')}
              >
                Staging
              </StyledButton>

              <Divider />

              <StyledTextInput
                label="Local Server IP"
                // Use defaultValue to prevent flickering in a portal
                defaultValue={inputIP}
                onChangeText={setInputIP}
                placeholder="e.g. 192.168.1.100"
                autoCapitalize="none"
              />

              <StyledButton
                mode="contained"
                onPress={() => onPressSelectEnvironment('local')}
              >
                Local
              </StyledButton>
            </KeyboardAwareScrollView>
          </MenuContainer>
        </Overlay>
      </Modal>
    </Portal>
  );
};

export default DevMenuModal;

const modalContentContainerStyle: ViewStyle = {
  flex: 1,
  height: '100%',
};

const Overlay = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.25)',
});

const MenuContainer = styled(Surface)({
  width: 300,
  padding: 20,
  backgroundColor: sand,
  borderRadius: 10,
  alignItems: 'center',
});

const Title = styled(Text)({
  fontSize: 24,
  fontWeight: 'bold',
  marginTop: 24,
  marginBottom: 15,
});

const CopyRow = styled.View({
  flexDirection: 'row',
  width: '100%',
  marginBottom: 10,
});

const Label = styled(Text)({
  fontSize: 14,
  fontWeight: 'bold',
  marginRight: 8,
});

const BodyText = styled(Text)({
  fontSize: 14,
});

const StyledButton = styled(Button)({
  width: '100%',
  marginTop: 10,
});

const Divider = styled.View({
  width: '100%',
  height: 1,
  backgroundColor: disabledGrey,
  marginVertical: 16,
});

const StyledTextInput = styled(TextInput)({
  width: '100%',
  marginBottom: 15,
});

const CloseButton = styled.TouchableOpacity({
  position: 'absolute',
  right: 0,
  justifyContent: 'center',
  alignItems: 'center',
});

const CloseIcon = styled.Image({
  width: 24,
  height: 24,
  resizeMode: 'contain',
  tintColor: black,
});
